import { formatUrl, route } from './route';

const forClient = (clientId: number) => ({ id: String(clientId) });

const forStrategy = (clientId: number, strategyId: number) => ({
    id: String(clientId),
    strategyId: String(strategyId)
});

export const clientRoutes = {
    list: (params?: URLSearchParams) => formatUrl(route('/(app)/clients'), params),
    create: () => route('/(app)/clients/new'),
    detail: (clientId: number) => route('/(app)/clients/[id=integer]', forClient(clientId)),
    fields: (clientId: number) => route('/(app)/clients/[id=integer]/fields', forClient(clientId)),
    lenderFields: (clientId: number) => route('/(app)/clients/[id=integer]/lenderfields', forClient(clientId)),
    portal: (clientId: number) => route('/(app)/clients/[id=integer]/portal', forClient(clientId)),
    jobHistory: (clientId: number) => route('/(app)/clients/[id=integer]/jobhistory', forClient(clientId)),
    jobHistoryRun: (clientId: number, activityId: number) =>
        route('/(app)/clients/[id=integer]/jobhistory/[activityId=integer]', {
            id: String(clientId),
            activityId: String(activityId)
        }),
    liveJobs: (clientId: number) => route('/(app)/clients/[id=integer]/livejobs', forClient(clientId)),
    autopayPreview: (clientId: number) => route('/(app)/clients/[id=integer]/autopay-preview', forClient(clientId)),
    auditHistory: (clientId: number) => route('/(app)/clients/[id=integer]/audit-history', forClient(clientId)),
    creditReports: (clientId: number) => route('/(app)/clients/[id=integer]/credit-reports', forClient(clientId)),
    logs: (clientId: number, params?: URLSearchParams) =>
        formatUrl(route('/(app)/clients/[id=integer]/logs', forClient(clientId)), params),
    obligations: (clientId: number, params?: URLSearchParams) =>
        formatUrl(route('/(app)/clients/[id=integer]/obligations', forClient(clientId)), params),
    obligors: (clientId: number, params?: URLSearchParams) =>
        formatUrl(route('/(app)/clients/[id=integer]/obligors', forClient(clientId)), params),

    collections: {
        universe: (clientId: number) => route('/(app)/clients/[id=integer]/collections/universe', forClient(clientId)),
        dashboard: (clientId: number) =>
            route('/(app)/clients/[id=integer]/collections/dashboard', forClient(clientId)),
        assignments: (clientId: number) =>
            route('/(app)/clients/[id=integer]/collections/assignments', forClient(clientId)),
        compliance: (clientId: number) =>
            route('/(app)/clients/[id=integer]/collections/compliance', forClient(clientId)),
        teams: (clientId: number) => route('/(app)/clients/[id=integer]/collections/teams', forClient(clientId)),
        team: (clientId: number, teamId: number) =>
            route('/(app)/clients/[id=integer]/collections/teams/[teamId]', {
                id: String(clientId),
                teamId: String(teamId)
            }),
        strategies: (clientId: number) =>
            route('/(app)/clients/[id=integer]/collections/strategies', forClient(clientId)),
        strategy: (clientId: number, strategyId: number) =>
            route('/(app)/clients/[id=integer]/collections/strategies/[strategyId]', forStrategy(clientId, strategyId)),
        strategyQueues: (clientId: number, strategyId: number) =>
            route(
                '/(app)/clients/[id=integer]/collections/strategies/[strategyId]/queues',
                forStrategy(clientId, strategyId)
            ),
        strategyCampaign: (clientId: number, strategyId: number) =>
            route(
                '/(app)/clients/[id=integer]/collections/strategies/[strategyId]/campaign',
                forStrategy(clientId, strategyId)
            ),
        strategyDashboard: (clientId: number, strategyId: number) =>
            route(
                '/(app)/clients/[id=integer]/collections/strategies/[strategyId]/dashboard',
                forStrategy(clientId, strategyId)
            ),
        strategyWaterfallRuns: (clientId: number, strategyId: number) =>
            route(
                '/(app)/clients/[id=integer]/collections/strategies/[strategyId]/waterfall-runs',
                forStrategy(clientId, strategyId)
            ),
        strategyWaterfallRun: (clientId: number, strategyId: number, runId: number) =>
            route('/(app)/clients/[id=integer]/collections/strategies/[strategyId]/waterfall-runs/[runId=integer]', {
                ...forStrategy(clientId, strategyId),
                runId: String(runId)
            })
    },

    treasury: {
        overview: (clientId: number) => route('/(app)/clients/[id=integer]/treasury', forClient(clientId)),
        accounts: (clientId: number) => route('/(app)/clients/[id=integer]/treasury/accounts', forClient(clientId)),
        accountCreate: (clientId: number) =>
            route('/(app)/clients/[id=integer]/treasury/accounts/new', forClient(clientId)),
        account: (clientId: number, lenderBankId: number) =>
            route('/(app)/clients/[id=integer]/treasury/accounts/[lenderBankId=integer]', {
                id: String(clientId),
                lenderBankId: String(lenderBankId)
            }),
        accountBalances: (clientId: number, lenderBankId: number) =>
            route('/(app)/clients/[id=integer]/treasury/accounts/[lenderBankId=integer]/balances', {
                id: String(clientId),
                lenderBankId: String(lenderBankId)
            }),
        payments: (clientId: number, params?: URLSearchParams) =>
            formatUrl(route('/(app)/clients/[id=integer]/treasury/payments', forClient(clientId)), params),
        summaryPayments: (clientId: number, params?: URLSearchParams) =>
            formatUrl(route('/(app)/clients/[id=integer]/treasury/summary-payments', forClient(clientId)), params),
        balancing: (clientId: number) => route('/(app)/clients/[id=integer]/treasury/balancing', forClient(clientId)),
        balancingPayments: (clientId: number, params?: URLSearchParams) =>
            formatUrl(route('/(app)/clients/[id=integer]/treasury/balancing/payments', forClient(clientId)), params),
        balancingTransfer: (clientId: number, params?: URLSearchParams) =>
            formatUrl(route('/(app)/clients/[id=integer]/treasury/balancing/transfer', forClient(clientId)), params)
    },

    dataSources: {
        list: (clientId: number) => route('/(app)/clients/[id=integer]/datasources', forClient(clientId)),
        create: (clientId: number) => route('/(app)/clients/[id=integer]/datasources/new', forClient(clientId)),
        edit: (clientId: number, dataSourceId: number) =>
            route('/(app)/clients/[id=integer]/datasources/edit/[datasourceid=integer]', {
                ...forClient(clientId),
                datasourceid: String(dataSourceId)
            })
    },

    encryptionKeys: {
        list: (clientId: number) => route('/(app)/clients/[id=integer]/encryptionkeys', forClient(clientId)),
        create: (clientId: number) => route('/(app)/clients/[id=integer]/encryptionkeys/new', forClient(clientId)),
        edit: (clientId: number, encryptionKeyId: number) =>
            route('/(app)/clients/[id=integer]/encryptionkeys/edit/[encryptionkeyid=integer]', {
                ...forClient(clientId),
                encryptionkeyid: String(encryptionKeyId)
            })
    },

    lenders: {
        list: (clientId: number) => route('/(app)/clients/[id=integer]/lenders', forClient(clientId)),
        create: (clientId: number) => route('/(app)/clients/[id=integer]/lenders/new', forClient(clientId)),
        edit: (clientId: number, lenderId: number) =>
            route('/(app)/clients/[id=integer]/lenders/[lenderId=integer]/edit', {
                ...forClient(clientId),
                lenderId: String(lenderId)
            }),
        fields: (clientId: number, lenderId: number) =>
            route('/(app)/clients/[id=integer]/lenders/[lenderId=integer]/fields', {
                ...forClient(clientId),
                lenderId: String(lenderId)
            })
    },

    statuses: {
        list: (clientId: number) => route('/(app)/clients/[id=integer]/status', forClient(clientId)),
        create: (clientId: number) => route('/(app)/clients/[id=integer]/status/new', forClient(clientId)),
        edit: (clientId: number, statusCode: string) =>
            route('/(app)/clients/[id=integer]/status/edit/[[statusCode]]', {
                ...forClient(clientId),
                statusCode
            })
    },

    users: {
        list: (clientId: number) => route('/(app)/clients/[id=integer]/users', forClient(clientId)),
        detail: (clientId: number, userId: number) =>
            route('/(app)/clients/[id=integer]/users/[userid=integer]', {
                ...forClient(clientId),
                userid: String(userId)
            }),
        create: (clientId: number) =>
            route('/(app)/clients/[id=integer]/users/edit/[[userid=integer]]', forClient(clientId)),
        createServiceUser: (clientId: number) =>
            route('/(app)/clients/[id=integer]/users/service-user', forClient(clientId)),
        edit: (clientId: number, userId: number) =>
            route('/(app)/clients/[id=integer]/users/edit/[[userid=integer]]', {
                ...forClient(clientId),
                userid: String(userId)
            })
    },

    reports: {
        list: (clientId: number) => route('/(app)/clients/[id=integer]/reports', forClient(clientId)),
        detail: (clientId: number, reportDefinitionId: number) =>
            route('/(app)/clients/[id=integer]/reports/[reportdefinitionid=integer]', {
                ...forClient(clientId),
                reportdefinitionid: String(reportDefinitionId)
            })
    },

    trueBi: (clientId: number) => route('/(app)/clients/[id=integer]/truebi', forClient(clientId))
};
